import { create } from "zustand";
import { imageAttachmentLabel } from "../ai/imageAttachments";

export const MAX_CHAT_SESSIONS = 50;
export const DEFAULT_CHAT_SESSION_TITLE = "新对话";
export const MAX_CHAT_SESSION_TIMESTAMP = 8_640_000_000_000_000 - 1;

export type ChatSessionMessage = {
  id: string;
  role: "user" | "assistant" | "system";
  parts: unknown[];
  metadata?: unknown;
};

export type ChatSessionRecord = {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: ChatSessionMessage[];
};

type ChatSessionState = {
  sessions: ChatSessionRecord[];
  activeSessionId: string;
  /** Active-session mirror used by project export and existing consumers. */
  messages: ChatSessionMessage[];
  /** Changes whenever the entire project workspace is replaced. */
  workspaceGeneration: number;
  /** Incremented when the active session changes outside useChat. */
  importGeneration: number;
  applyingImport: boolean;
  publishFromUi: (
    sessionId: string,
    workspaceGeneration: number,
    messages: readonly ChatSessionMessage[],
  ) => void;
  createSession: (
    messages?: readonly ChatSessionMessage[],
    title?: string,
  ) => string;
  importSession: (messages: readonly ChatSessionMessage[]) => string;
  switchSession: (sessionId: string) => void;
  deleteSession: (sessionId: string) => void;
  restoreWorkspace: (
    sessions: readonly ChatSessionRecord[],
    activeSessionId?: string,
  ) => void;
  clear: () => void;
};

let fallbackId = 0;

function createSessionId(): string {
  if (
    typeof globalThis.crypto !== "undefined" &&
    "randomUUID" in globalThis.crypto
  ) {
    return globalThis.crypto.randomUUID();
  }
  fallbackId += 1;
  return `chat-${Date.now().toString(36)}-${fallbackId.toString(36)}`;
}

function cloneMessages(
  messages: readonly ChatSessionMessage[],
): ChatSessionMessage[] {
  return messages.map((message) => ({
    id: message.id,
    role: message.role,
    parts: message.parts,
    metadata: message.metadata,
  }));
}

function messagesAreUnchanged(
  current: readonly ChatSessionMessage[],
  incoming: readonly ChatSessionMessage[],
): boolean {
  return (
    current.length === incoming.length &&
    current.every((message, index) => {
      const next = incoming[index];
      return (
        next !== undefined &&
        message.id === next.id &&
        message.role === next.role &&
        message.parts === next.parts &&
        message.metadata === next.metadata
      );
    })
  );
}

function nextSessionTimestamps(
  session: Pick<ChatSessionRecord, "createdAt" | "updatedAt">,
): Pick<ChatSessionRecord, "createdAt" | "updatedAt"> {
  const now = Math.min(Date.now(), MAX_CHAT_SESSION_TIMESTAMP);
  const createdAt =
    Number.isSafeInteger(session.createdAt) &&
    session.createdAt >= 0 &&
    session.createdAt <= MAX_CHAT_SESSION_TIMESTAMP
      ? session.createdAt
      : now;
  if (
    !Number.isSafeInteger(session.updatedAt) ||
    session.updatedAt < createdAt ||
    session.updatedAt >= MAX_CHAT_SESSION_TIMESTAMP
  ) {
    return {
      createdAt: Math.min(createdAt, now),
      updatedAt: now,
    };
  }
  const updatedAt = Math.min(
    MAX_CHAT_SESSION_TIMESTAMP,
    Math.max(now, session.updatedAt + 1),
  );
  return {
    createdAt: Math.min(createdAt, updatedAt),
    updatedAt,
  };
}

export function deriveChatSessionTitle(
  messages: readonly ChatSessionMessage[],
): string {
  let imageTitle: string | null = null;
  for (const message of messages) {
    if (message.role !== "user") continue;
    for (const part of message.parts) {
      imageTitle ??= imageAttachmentLabel(part);
      if (
        typeof part !== "object" ||
        part === null ||
        (part as { type?: unknown }).type !== "text" ||
        typeof (part as { text?: unknown }).text !== "string"
      ) {
        continue;
      }
      const text = (part as { text: string }).text.replace(/\s+/g, " ").trim();
      if (!text) continue;
      return text.length > 28 ? `${text.slice(0, 27)}…` : text;
    }
  }
  if (imageTitle) {
    const title = `图片 · ${imageTitle}`;
    return title.length > 28 ? `${title.slice(0, 27)}…` : title;
  }
  return DEFAULT_CHAT_SESSION_TITLE;
}

function createSessionRecord(
  messages: readonly ChatSessionMessage[] = [],
  title?: string,
): ChatSessionRecord {
  const now = Date.now();
  const cloned = cloneMessages(messages);
  return {
    id: createSessionId(),
    title: title?.trim() || deriveChatSessionTitle(cloned),
    createdAt: now,
    updatedAt: now,
    messages: cloned,
  };
}

function cloneSession(session: ChatSessionRecord): ChatSessionRecord {
  return {
    id: session.id,
    title: session.title.trim() || deriveChatSessionTitle(session.messages),
    createdAt: session.createdAt,
    updatedAt: session.updatedAt,
    messages: cloneMessages(session.messages),
  };
}

function finishImport(set: (state: Partial<ChatSessionState>) => void): void {
  queueMicrotask(() => set({ applyingImport: false }));
}

const initialSession = createSessionRecord();

export const useChatSessionStore = create<ChatSessionState>((set, get) => ({
  sessions: [initialSession],
  activeSessionId: initialSession.id,
  messages: [],
  workspaceGeneration: 0,
  importGeneration: 0,
  applyingImport: false,

  publishFromUi: (sessionId, workspaceGeneration, messages) => {
    const current = get();
    if (current.workspaceGeneration !== workspaceGeneration) return;
    const session = current.sessions.find((entry) => entry.id === sessionId);
    if (!session) return;
    if (messagesAreUnchanged(session.messages, messages)) return;

    const cloned = cloneMessages(messages);
    const updated: ChatSessionRecord = {
      ...session,
      ...nextSessionTimestamps(session),
      title: deriveChatSessionTitle(cloned),
      messages: cloned,
    };
    set({
      sessions: [
        updated,
        ...current.sessions.filter((entry) => entry.id !== sessionId),
      ],
      messages:
        current.activeSessionId === sessionId ? cloned : current.messages,
    });
  },

  createSession: (messages = [], title) => {
    const current = get();
    const active = current.sessions.find(
      (session) => session.id === current.activeSessionId,
    );
    const cloned = cloneMessages(messages);

    if (active && active.messages.length === 0) {
      const updated: ChatSessionRecord = {
        ...active,
        ...nextSessionTimestamps(active),
        title: title?.trim() || deriveChatSessionTitle(cloned),
        messages: cloned,
      };
      set({
        sessions: [
          updated,
          ...current.sessions.filter((session) => session.id !== active.id),
        ],
        messages: cloned,
        importGeneration: current.importGeneration + 1,
        applyingImport: true,
      });
      finishImport(set);
      return active.id;
    }

    if (current.sessions.length >= MAX_CHAT_SESSIONS) {
      throw new Error(
        `会话历史已达 ${MAX_CHAT_SESSIONS} 条，请删除旧会话后再新建。`,
      );
    }

    const session = createSessionRecord(cloned, title);
    set({
      sessions: [session, ...current.sessions],
      activeSessionId: session.id,
      messages: session.messages,
      importGeneration: current.importGeneration + 1,
      applyingImport: true,
    });
    finishImport(set);
    return session.id;
  },

  importSession: (messages) => get().createSession(messages),

  switchSession: (sessionId) => {
    const current = get();
    if (sessionId === current.activeSessionId) return;
    const session = current.sessions.find((entry) => entry.id === sessionId);
    if (!session) return;
    const messages = cloneMessages(session.messages);
    set({
      sessions: [
        session,
        ...current.sessions.filter((entry) => entry.id !== sessionId),
      ],
      activeSessionId: sessionId,
      messages,
      importGeneration: current.importGeneration + 1,
      applyingImport: true,
    });
    finishImport(set);
  },

  deleteSession: (sessionId) => {
    const current = get();
    if (!current.sessions.some((session) => session.id === sessionId)) return;
    const remaining = current.sessions.filter(
      (session) => session.id !== sessionId,
    );

    if (sessionId !== current.activeSessionId) {
      set({ sessions: remaining });
      return;
    }

    const next = remaining[0] ?? createSessionRecord();
    const messages = cloneMessages(next.messages);
    set({
      sessions: remaining.length > 0 ? remaining : [next],
      activeSessionId: next.id,
      messages,
      importGeneration: current.importGeneration + 1,
      applyingImport: true,
    });
    finishImport(set);
  },

  restoreWorkspace: (sessions, requestedActiveSessionId) => {
    const restored = sessions
      .slice(0, MAX_CHAT_SESSIONS)
      .map((session) => cloneSession(session));
    const normalized = restored.length > 0 ? restored : [createSessionRecord()];
    const active =
      normalized.find((session) => session.id === requestedActiveSessionId) ??
      normalized[0];
    const messages = cloneMessages(active.messages);
    set({
      sessions: normalized,
      activeSessionId: active.id,
      messages,
      workspaceGeneration: get().workspaceGeneration + 1,
      importGeneration: get().importGeneration + 1,
      applyingImport: true,
    });
    finishImport(set);
  },

  clear: () => {
    const session = createSessionRecord();
    set({
      sessions: [session],
      activeSessionId: session.id,
      messages: [],
      workspaceGeneration: get().workspaceGeneration + 1,
      importGeneration: get().importGeneration + 1,
      applyingImport: true,
    });
    finishImport(set);
  },
}));

export function resetChatSessionStoreForTests(): void {
  const session = createSessionRecord();
  useChatSessionStore.setState({
    sessions: [session],
    activeSessionId: session.id,
    messages: [],
    workspaceGeneration: useChatSessionStore.getState().workspaceGeneration + 1,
    importGeneration: 0,
    applyingImport: false,
  });
}
